/**
 * schemaV2.js — Schema-v2 form contracts, tree helpers, and payload builder.
 * Pure logic only, no React.
 */
import { z } from 'zod';

// ─── Regex helpers ─────────────────────────────────────────────────────────────
export const SYMBOL_FORMAT_RE = /^[A-Z]{2,10}:[^\s:]+$/;
const TIME_RE = /^\d{2}:\d{2}$/;

// ─── Zod Schemas ───────────────────────────────────────────────────────────────

const globalRulesSchema = z.object({
  trading_mode:          z.enum(['INTRADAY', 'POSITIONAL']).default('INTRADAY'),
  start_time:            z.string().regex(TIME_RE, 'Use HH:MM').default('09:15'),
  end_time:              z.string().regex(TIME_RE, 'Use HH:MM').default('15:20'),
  no_entry_after:        z.string().regex(TIME_RE, 'Use HH:MM').default('15:00'),
  no_trade_windows:          z.array(z.any()).default([]),
  allow_long:                z.boolean().default(true),
  allow_short:               z.boolean().default(true),
  entry_conflict_policy:     z.enum(['SKIP_BOTH', 'LONG_PRIORITY', 'SHORT_PRIORITY']).default('SKIP_BOTH'),
  opposite_signal_handling:  z.enum(['EXIT_AND_REVERSE', 'WAIT_FOR_EXIT']).default('EXIT_AND_REVERSE'),
});

const riskComponentSchema = z.object({
  type:     z.enum(['percent', 'points']).default('percent'),
  value:    z.coerce.number().min(0, 'Value must be 0 or greater').default(0),
  apply_on: z.enum(['UNDERLYING', 'OPTION_PREMIUM']).default('UNDERLYING'),
});

const riskSchema = z.object({
  target:        riskComponentSchema,
  stop_loss:     riskComponentSchema,
  trailing_stop: riskComponentSchema,
});

const executionSchema = z.object({
  instrument: z.enum(['UNDERLYING', 'OPTIONS']).default('UNDERLYING'),
  allow_pyramiding: z.boolean().default(false),
  max_pyramid_legs: z.coerce.number().int().min(1).max(20).default(5),
  allow_partial_exit: z.boolean().default(false),
  product_type: z.enum(['CNC', 'INTRADAY', 'MARGIN', 'MTF']).nullable().optional(),
  trade_in_next_exp: z.enum(['OFF', 'ON_EXPIRY_DAY', 'ONE_DAY_BEFORE_EXP']).catch('OFF').default('OFF'),
  entry_order: z.object({
    order_type: z.enum(['MARKET', 'LIMIT', 'SL-M', 'SL-L']).default('MARKET'),
    mpp: z.coerce.number().min(0).max(100).default(2),
    price_mode: z.enum(['ABSOLUTE', 'OFFSET_POINTS', 'OFFSET_PERCENTAGE']).default('OFFSET_POINTS'),
    limit_price_value: z.coerce.number().nullable().optional(),
    trigger_price_value: z.coerce.number().nullable().optional(),
  }).superRefine((value, ctx) => {
    if (value.order_type !== 'SL-L') return;
    if (!value.price_mode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['price_mode'],
        message: 'Limit Price Mode is required for SL-L.',
      });
    }
    if (value.limit_price_value == null || !Number.isFinite(value.limit_price_value) || value.limit_price_value <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['limit_price_value'],
        message: 'Limit Price must be greater than 0.',
      });
    }
  }).default({
    order_type: 'MARKET',
    mpp: 2,
    price_mode: 'OFFSET_POINTS',
    limit_price_value: null,
    trigger_price_value: null,
  }),
  quantity: z.object({
    mode: z.enum(['FIXED_QTY', 'BY_MARGIN', 'BY_RISK', 'BY_ALERT']).default('FIXED_QTY'),
    fixed_qty: z.coerce.number().int().positive().nullable().optional(),
    margin_per_trade: z.coerce.number().positive().nullable().optional(),
    risk_amount: z.coerce.number().positive().nullable().optional(),
  }).superRefine((value, ctx) => {
    if (value.mode === 'FIXED_QTY' && !value.fixed_qty) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fixed_qty'],
        message: 'Fixed Qty is required.',
      });
    }
    if (value.mode === 'BY_MARGIN' && !value.margin_per_trade) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['margin_per_trade'],
        message: 'Margin / Trade is required.',
      });
    }
    if (value.mode === 'BY_RISK' && !value.risk_amount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['risk_amount'],
        message: 'Risk Amount is required.',
      });
    }
  }).nullable().optional(),
});

const sectionFlagSchema = z.object({
  enabled: z.boolean().default(false),
});

const sectionsEnabledSchema = z.object({
  long_entry:  sectionFlagSchema,
  long_exit:   sectionFlagSchema,
  short_entry: sectionFlagSchema,
  short_exit:  sectionFlagSchema,
});

export const formSchemaV2 = z.object({
  name:              z.string().min(1, 'Required').max(100),
  description:       z.string().optional().nullable(),
  symbol:            z.string().min(1, 'Symbol required').regex(SYMBOL_FORMAT_RE, 'Format: EXCHANGE:SYMBOL_CODE'),
  primary_timeframe: z.enum(['1m', '3m', '5m', '10m', '15m', '30m', '1h', '1d']).default('5m'),
  preferred_execution_mode: z.enum(['PAPER', 'LIVE']).nullable().optional(),
  global_rules:      globalRulesSchema,
  risk:              riskSchema,
  execution:         executionSchema,
  sections:           sectionsEnabledSchema,
  deploy_after_create: z.boolean().default(false),
  scheduler_enabled:   z.boolean().default(false),
  schedule_days_mask:  z.number().int().min(1).max(31).default(31),
});

// ─── Defaults ──────────────────────────────────────────────────────────────────

export const DEFAULTS_V2 = {
  name: '',
  description: '',
  symbol: 'NSE:SBIN-EQ',
  primary_timeframe: '5m',
  preferred_execution_mode: null,
  global_rules: {
    trading_mode: 'INTRADAY',
    start_time: '09:15',
    end_time: '15:20',
    no_entry_after: '15:00',
    no_trade_windows: [],
    allow_long: true,
    allow_short: true,
    entry_conflict_policy: 'SKIP_BOTH',
    opposite_signal_handling: 'EXIT_AND_REVERSE',
  },
  risk: {
    target:        { type: 'percent', value: 0, apply_on: 'UNDERLYING' },
    stop_loss:     { type: 'percent', value: 0, apply_on: 'UNDERLYING' },
    trailing_stop: { type: 'percent', value: 0, apply_on: 'UNDERLYING' },
  },
  execution: {
    instrument: 'UNDERLYING',
    allow_pyramiding: false,
    max_pyramid_legs: 5,
    allow_partial_exit: false,
    product_type: null,
    trade_in_next_exp: 'OFF',
    entry_order: {
      order_type: 'MARKET',
      mpp: 2,
      price_mode: 'OFFSET_POINTS',
      limit_price_value: null,
      trigger_price_value: null,
    },
    quantity: {
      mode: 'FIXED_QTY',
      fixed_qty: 1,
      margin_per_trade: null,
      risk_amount: null,
    },
  },
  sections: {
    long_entry:  { enabled: false },
    long_exit:   { enabled: false },
    short_entry: { enabled: false },
    short_exit:  { enabled: false },
  },
  deploy_after_create: false,
  scheduler_enabled:   false,
  schedule_days_mask:  31,
};

export function buildExternalPlaceholderSymbol(exchange = 'NSE') {
  const token = String(exchange || 'NSE').trim().toUpperCase();
  const normalized = ['NSE', 'BSE', 'MCX'].includes(token) ? token : 'NSE';
  return `${normalized}:EXTERNAL`;
}

export function buildManualPlaceholderSymbol(exchange = 'NSE') {
  const token = String(exchange || 'NSE').trim().toUpperCase();
  const normalized = ['NSE', 'BSE', 'MCX'].includes(token) ? token : 'NSE';
  return `${normalized}:MANUAL`;
}

// ── Manual Strategy payload ──────────────────────────────────────────────────
// Strategy-level template only. Per-symbol qty/risk/order/Target/SL live in the
// Manual Trading watchlist — NOT here. No risk{}, no sections, no qty value, no
// webhook/token. Vendor is hidden in the UI but stored internally for symbol
// resolution. `strategy_source:'MANUAL'` drives existing builder/list/preview
// plumbing; `source`/`category`/`manual_trading` carry the semantic intent.
export function buildManualStrategyPayload(formData, manual) {
  const exchange = manual?.exchange || 'NSE';
  const tradeIn = manual?.tradeIn || 'OPTION';
  const isOption = tradeIn === 'OPTION';
  const lotSize = Math.max(1, Number(manual?.lotSize) || 1);

  // Reuse the External/Internal option-mapping payload shape verbatim.
  const optionLegs = isOption
    ? {
        bullish_signal_legs: (manual?.optionMapping?.bullish || []).map((leg) => mapUiLegToExecutionLeg(leg, lotSize)),
        bearish_signal_legs: (manual?.optionMapping?.bearish || []).map((leg) => mapUiLegToExecutionLeg(leg, lotSize)),
      }
    : {};

  return {
    schema_version: '2.0',
    strategy_source: 'MANUAL',
    source: 'MANUAL',
    category: 'MANUAL_TRADING',
    symbol_format: 'FYERS_CANONICAL',
    data_vendor: 'FYERS',
    strategy_meta: {
      name: formData.name,
      symbol: buildManualPlaceholderSymbol(exchange), // compiler-safe placeholder
      primary_timeframe: formData.primary_timeframe || '5m',
      ...(formData.description ? { description: formData.description } : {}),
    },
    manual_trading: {
      enabled: true,
      panel_signal_allowed: true,
      exchange,
      segment: manual?.segment || 'FNO',
      trade_in: tradeIn,                          // UNDERLYING | OPTION
      product_type: manual?.productType || 'INTRADAY',
      qty_mode: manual?.qtyMode || 'FIXED_QTY',   // FIXED_QTY | BY_MARGIN (mode only, no value)
      pyramiding: !!manual?.pyramiding,
    },
    global_rules: {
      trading_mode: formData.global_rules.trading_mode,
      start_time:   formData.global_rules.start_time,
      end_time:     formData.global_rules.end_time,
      ...(formData.global_rules.no_entry_after ? { no_entry_after: formData.global_rules.no_entry_after } : {}),
    },
    // Mirror canonical fields for generic consumers (manual_trading is the source of
    // truth). instrument uses the External vocabulary ('OPTIONS') so option legs +
    // edit-hydration reuse the existing path. No quantity value, no risk{}.
    execution: {
      instrument:   isOption ? 'OPTIONS' : 'UNDERLYING',
      product_type: manual?.productType || 'INTRADAY',
      allow_pyramiding: !!manual?.pyramiding,
      trade_in_next_exp: isOption ? normalizeTradeInNextExp(manual?.tradeInNextExp) : 'OFF',
      ...optionLegs,
    },
  };
}

function toOptionalNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function defaultProductTypeForInstrument(instrument = 'UNDERLYING') {
  return instrument === 'OPTIONS' ? 'MARGIN' : 'INTRADAY';
}

function normalizePreferredExecutionMode(value) {
  const token = String(value || '').trim().toUpperCase();
  if (token === 'LIVE') return 'LIVE';
  if (token === 'PAPER') return 'PAPER';
  return null;
}

function normalizeQuantityPayload(quantityConfig) {
  if (!quantityConfig) return null;
  const mode = quantityConfig.mode || (
    quantityConfig.margin_per_trade != null ? 'BY_MARGIN' : (
      quantityConfig.risk_amount != null ? 'BY_RISK' : 'FIXED_QTY'
    )
  );
  if (mode === 'FIXED_QTY') {
    const fixedQty = toOptionalNumber(quantityConfig.fixed_qty);
    return fixedQty ? { mode, fixed_qty: fixedQty } : null;
  }
  if (mode === 'BY_MARGIN') {
    const marginPerTrade = toOptionalNumber(quantityConfig.margin_per_trade);
    return marginPerTrade ? { mode, margin_per_trade: marginPerTrade } : null;
  }
  if (mode === 'BY_RISK') {
    const riskAmount = toOptionalNumber(quantityConfig.risk_amount);
    return riskAmount ? { mode, risk_amount: riskAmount } : null;
  }
  if (mode === 'BY_ALERT') {
    return { mode };
  }
  return null;
}

function normalizeOppositeSignalHandling(globalRules = {}) {
  return (
    globalRules.opposite_signal_handling
    || globalRules.opposite_signal_policy
    || 'EXIT_AND_REVERSE'
  );
}

function isLegacyStopConfig(entryOrder = {}) {
  if (!entryOrder || typeof entryOrder !== 'object') return false;
  const orderType = entryOrder.order_type;
  if (orderType === 'SL-M') {
    return entryOrder.trigger_price_value != null;
  }
  if (orderType === 'SL-L') {
    return entryOrder.price_mode === 'ABSOLUTE' || entryOrder.trigger_price_value != null;
  }
  if (orderType === 'LIMIT') {
    return entryOrder.price_mode != null
      || entryOrder.limit_price_value != null
      || entryOrder.trigger_price_value != null;
  }
  return false;
}

// ─── Comparators ───────────────────────────────────────────────────────────────

export const COMPARATOR_MAP_V1_TO_V2 = {
  '>': 'GT', '<': 'LT', '>=': 'GTE', '<=': 'LTE',
  '==': 'EQ', '!=': 'NEQ',
  'crosses_above': 'CROSS_ABOVE', 'crosses_below': 'CROSS_BELOW',
};

export const COMPARATOR_OPTIONS = [
  { value: 'GT',          label: '>'          },
  { value: 'GTE',         label: '>='         },
  { value: 'LT',          label: '<'          },
  { value: 'LTE',         label: '<='         },
  { value: 'EQ',          label: '=='         },
  { value: 'CROSS_ABOVE', label: 'Crosses ↑'  },
  { value: 'CROSS_BELOW', label: 'Crosses ↓'  },
];

export const CROSS_COMPARATORS = new Set(['CROSS_ABOVE', 'CROSS_BELOW']);

export const TRADE_IN_NEXT_EXP_OPTIONS = [
  { value: 'OFF',                label: 'OFF' },
  { value: 'ON_EXPIRY_DAY',      label: 'ON EXPIRY DAY' },
  { value: 'ONE_DAY_BEFORE_EXP', label: 'ONE DAY BEFORE EXP' },
];

export function normalizeTradeInNextExp(v) {
  return ['OFF', 'ON_EXPIRY_DAY', 'ONE_DAY_BEFORE_EXP'].includes(v) ? v : 'OFF';
}

// ─── Indicator catalog ─────────────────────────────────────────────────────────

export const INDICATOR_DEFAULTS = {
  SMA:        { params: { length: 20 },                              outputs: null },
  EMA:        { params: { length: 20 },                              outputs: null },
  RSI:        { params: { length: 14 },                              outputs: null },
  MACD:       { params: { fast: 12, slow: 26, signal_period: 9 },    outputs: ['macd', 'signal', 'histogram'] },
  VWAP:       { params: {},                                           outputs: null },
  ADX:        { params: { length: 14 },                              outputs: null },
  ATR:        { params: { length: 14 },                              outputs: null },
  SUPERTREND: { params: { length: 10, multiplier: 3 },               outputs: null },
};

export const PRICE_NAMES = [
  'open', 'high', 'low', 'close', 'ltp',
  'volume', 'range', 'body',
  'upper_wick', 'lower_wick',
  'body_pct_range', 'upper_wick_pct_range', 'lower_wick_pct_range',
];
export const INDICATOR_NAMES = Object.keys(INDICATOR_DEFAULTS);
export const TIMEFRAME_OPTIONS = ['1m', '3m', '5m', '10m', '15m', '30m', '1h', '1d'];

// ─── Tree — ID management ──────────────────────────────────────────────────────

function genId() {
  return `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

/** Attach transient _id to every node for React keying (never sent to backend) */
export function attachIds(node) {
  if (!node) return node;
  if (node.node_type === 'group') {
    return { ...node, _id: node._id || genId(), children: (node.children || []).map(attachIds) };
  }
  return { ...node, _id: node._id || genId() };
}

/** Strip transient _id before sending to backend */
export function stripIds(node) {
  if (!node) return node;
  // eslint-disable-next-line no-unused-vars
  const { _id, ...rest } = node;
  if (rest.node_type === 'group') {
    return { ...rest, children: (rest.children || []).map(stripIds) };
  }
  return normalizeRuleNode(rest);
}

function normalizeOperandForPayload(operand) {
  if (!operand || operand.kind !== 'series') return operand;
  const normalized = { ...operand };
  if (normalized.source === 'price' && normalized.name === 'ltp') {
    normalized.timeframe = '1m';
    normalized.offset = 0;
    return normalized;
  }
  return normalized;
}

function normalizeRuleNode(node) {
  if (!node || node.node_type !== 'condition') return node;
  return {
    ...node,
    left: normalizeOperandForPayload(node.left),
    right: normalizeOperandForPayload(node.right),
  };
}

// ─── Tree — factory ───────────────────────────────────────────────────────────

/** Create an empty root group node */
export function emptyGroup(operator = 'AND') {
  return { _id: genId(), node_type: 'group', operator, children: [] };
}

function defaultConditionNode(timeframe = '5m') {
  return {
    _id: genId(),
    node_type: 'condition',
    left:  { kind: 'series', source: 'price',     name: 'close', timeframe, offset: 0 },
    comparator: 'GT',
    right: { kind: 'series', source: 'indicator', name: 'EMA',   params: { length: 20 }, timeframe, offset: 0 },
  };
}

// ─── Tree — immutable mutations ────────────────────────────────────────────────

/** Apply a mapper function to every node (depth-first, post-order) */
function mapNode(node, fn) {
  if (!node) return node;
  const mapped = fn(node);
  if (mapped.node_type === 'group') {
    return { ...mapped, children: (mapped.children || []).map((c) => mapNode(c, fn)) };
  }
  return mapped;
}

/** Add a new default condition to the root group */
export function addConditionToTree(tree, timeframe = '5m') {
  if (!tree) return emptyGroup();
  return { ...tree, children: [...(tree.children || []), defaultConditionNode(timeframe)] };
}

/** Add a new default condition inside the group with the given nodeId */
export function addConditionToNode(tree, nodeId, timeframe = '5m') {
  if (!tree) return emptyGroup();
  return mapNode(tree, (node) => {
    if (node._id !== nodeId || node.node_type !== 'group') return node;
    return { ...node, children: [...(node.children || []), defaultConditionNode(timeframe)] };
  });
}

/** Add an empty sub-group inside the group with the given nodeId */
export function addGroupToNode(tree, nodeId) {
  if (!tree) return emptyGroup();
  return mapNode(tree, (node) => {
    if (node._id !== nodeId || node.node_type !== 'group') return node;
    return { ...node, children: [...(node.children || []), emptyGroup()] };
  });
}

/** Change the AND/OR operator on a specific group node */
export function setGroupOperator(tree, nodeId, operator) {
  return mapNode(tree, (node) => {
    if (node._id !== nodeId || node.node_type !== 'group') return node;
    return { ...node, operator };
  });
}

/** Patch a specific node */
export function updateNodeInTree(tree, nodeId, patch) {
  return mapNode(tree, (node) => {
    if (node._id !== nodeId) return node;
    return { ...node, ...patch };
  });
}

/** Remove a node by _id (root cannot be removed; will simply be skipped) */
export function removeNodeFromTree(tree, nodeId) {
  if (!tree || tree.node_type !== 'group') return tree;
  return {
    ...tree,
    children: (tree.children || [])
      .filter((c) => c._id !== nodeId)
      .map((c) => (c.node_type === 'group' ? removeNodeFromTree(c, nodeId) : c)),
  };
}

// ─── Migration (v1 → v2) — stub, not wired in Phase 1 ─────────────────────────

export function migrateV1TreeToV2(v1Tree) {
  if (!v1Tree) return emptyGroup();
  const rootNode = v1Tree.root ?? v1Tree;
  return attachIds(migrateNode(rootNode) || emptyGroup());
}

function migrateNode(node) {
  if (!node) return null;
  if (node.node_type === 'group') {
    return {
      node_type: 'group',
      operator: node.operator || 'AND',
      children: (node.children || []).map(migrateNode).filter(Boolean),
    };
  }
  if (node.node_type === 'condition') {
    if (['time_check', 'position_check'].includes(node.condition_type)) return null;
    return {
      node_type: 'condition',
      comparator: COMPARATOR_MAP_V1_TO_V2[node.comparator] ?? node.comparator,
      left:  migrateOperand(node.left),
      right: migrateOperand(node.right),
    };
  }
  return null;
}

function migrateOperand(op) {
  if (!op) return { kind: 'constant', value: null };
  if (op.kind === 'candle_field')          return { kind: 'series', source: 'price',     name: op.field, timeframe: op.timeframe || '5m', offset: 0 };
  if (op.kind === 'previous_candle_field') return { kind: 'series', source: 'price',     name: op.field, timeframe: op.timeframe || '5m', offset: 1 };
  if (op.kind === 'indicator')             return { kind: 'series', source: 'indicator', name: op.name,  timeframe: op.timeframe || '5m', offset: 0, params: op.params ?? {} };
  if (op.kind === 'constant')              return { kind: 'constant', value: op.value };
  return op;
}

// ─── Leg mappers (moved from StrategyBuilder) ──────────────────────────────────

export function mapUiExpiryToExecution(uiExpiry) {
  if (uiExpiry === 'next_week')    return 'NEXT_WEEK';
  if (uiExpiry === 'next_month')   return 'NEXT_MONTH';
  if (uiExpiry === 'current_month') return 'CURRENT_MONTH';
  return 'CURRENT_WEEK';
}

export function mapUiExpiryToBackend(uiExpiry) {
  if (uiExpiry === 'current_week') return 'current_week';
  if (uiExpiry === 'next_week')    return 'next_week';
  return 'monthly';
}

export function mapLegToBackendStrike(leg) {
  if (!leg || leg.strikeSelectionMode !== 'manual') return 'ATM';
  if (leg.manualStrike === 'ATM') return 'ATM';
  const offset = Math.max(1, Number(leg.offset) || 1);
  if (leg.manualStrike === 'OTM') return offset >= 2 ? 'OTM2' : 'OTM1';
  if (leg.manualStrike === 'ITM') return offset >= 2 ? 'ITM2' : 'ITM1';
  return 'ATM';
}

function getExecutionPriority(action) {
  return String(action || '').toUpperCase() === 'BUY' ? 1 : 2;
}

export function mapUiLegToExecutionLeg(leg, fallbackLotSize = 1) {
  const isClosestPremium = leg.strikeSelectionMode === 'closest_premium';
  const strikeType = leg.manualStrike === 'ITM' ? 'ITM' : leg.manualStrike === 'OTM' ? 'OTM' : 'ATM';
  const lots     = Math.max(1, Number(leg.lots)    || 1);
  const lotSize  = Math.max(1, Number(leg.lotSize) || Number(fallbackLotSize) || 1);
  const quantity = Math.max(1, lots * lotSize);
  const premiumRaw = isClosestPremium ? Number(leg.premium) : NaN;
  return {
    action:               String(leg.action || 'BUY').toUpperCase() === 'SELL' ? 'SELL' : 'BUY',
    option_type:          String(leg.optionType || 'CE').toUpperCase() === 'PE' ? 'PE'   : 'CE',
    expiry_type:          mapUiExpiryToExecution(leg.expiry),
    strike_selection_mode: isClosestPremium ? 'CLOSEST_PREMIUM' : 'MANUAL',
    strike_type:          strikeType,
    offset:               strikeType === 'ATM' ? 0 : Math.max(1, Number(leg.offset) || 1),
    premium_target:       Number.isFinite(premiumRaw) && premiumRaw > 0 ? premiumRaw : null,
    lots,
    lot_size:             lotSize,
    quantity,
    leg_execution_priority: getExecutionPriority(leg.action),
  };
}

// ─── Leg reverse mapper (backend execution leg → UI leg) ──────────────────────

export function mapBackendExpiryToUi(expiryType) {
  if (expiryType === 'NEXT_WEEK')     return 'next_week';
  if (expiryType === 'NEXT_MONTH')    return 'next_month';
  if (expiryType === 'CURRENT_MONTH') return 'current_month';
  return 'current_week';
}

export function mapExecutionLegToUiLeg(leg) {
  return {
    id: `${Date.now()}_${Math.random().toString(16).slice(2, 8)}`,
    action:               String(leg.action      || 'BUY').toUpperCase(),
    optionType:           String(leg.option_type || 'CE').toUpperCase(),
    expiry:               mapBackendExpiryToUi(leg.expiry_type),
    strikeSelectionMode:  leg.strike_selection_mode === 'CLOSEST_PREMIUM' ? 'closest_premium' : 'manual',
    manualStrike:         leg.strike_type  || 'ATM',
    offset:               leg.offset       ?? 1,
    premium:              leg.premium_target ?? '',
    lots:                 leg.lots         ?? 1,
    lotSize:              leg.lot_size     ?? 1,
  };
}

// ─── Payload Builder ───────────────────────────────────────────────────────────

/**
 * Builds the complete schema-v2 POST payload.
 *
 * @param {object} formData        - react-hook-form getValues() result
 * @param {object} sectionTrees    - { long_entry, long_exit, short_entry, short_exit }
 * @param {object} optionMapping   - { bullish: [...legs], bearish: [...legs] }
 * @param {number} selectedLotSize
 * @param {object} [symbolUniverse]  - { mode, symbols, segment, preset_key }
 * @param {object} [sectionEntryConfig] - { long_entry, short_entry } execution+lifecycle config
 * @returns {object} schema-v2 envelope ready for POST
 */
export function buildSchemaV2Payload(
  formData,
  sectionTrees,
  optionMapping,
  selectedLotSize,
  symbolUniverse,
  sectionEntryConfig,
  strategySource = 'INTERNAL',
  externalSourceType = null,
  strategyToken = '',
  externalExchange = null,
  externalSegment = null,
  externalBrokerName = null,
  externalMode = 'SIMPLE',
) {
  const resolvedLotSize = Math.max(1, Number(selectedLotSize) || 1);
  const ENTRY_KEYS = new Set(['long_entry', 'short_entry']);

  // Build four sections — always emit all four keys.
  // All sections use top-level `tree`. Entry sections additionally include
  // `execution` and `lifecycle` blocks. Exit sections use { enabled, tree } only.
  const sections = {};
  for (const key of ['long_entry', 'long_exit', 'short_entry', 'short_exit']) {
    const enabled = formData.sections[key]?.enabled ?? false;
    if (!enabled) {
      sections[key] = { enabled: false };
      continue;
    }
    const tree        = sectionTrees[key];
    const hasChildren = tree && (tree.children || []).length > 0;
    if (!hasChildren) {
      throw new Error(
        `buildSchemaV2Payload: section "${key}" is enabled but has no conditions. Payload build aborted.`
      );
    }
    if (ENTRY_KEYS.has(key)) {
      const ec   = sectionEntryConfig?.[key] || {};
      const exec = ec.execution || {};
      const lc   = ec.lifecycle  || {};
      const n    = Math.max(1, parseInt(lc.expiry_candles, 10) || 3);
      sections[key] = {
        enabled: true,
        tree: stripIds(tree),
        execution: {
          mode:         exec.mode      || 'IMMEDIATE',
          reference:    exec.reference || 'PREVIOUS_CANDLE',
          level:        exec.level     || 'HIGH',
          confirmation: exec.mode === 'CROSS_AND_CLOSE' ? 'CLOSE' : 'NONE',
          ...(exec.mode === 'LTP_CROSS' && (exec.buffer ?? 0) > 0
            ? { buffer: exec.buffer, buffer_unit: exec.buffer_unit || 'points' }
            : {}),
        },
        lifecycle: {
          validity:       lc.validity    || 'UNTIL_TRIGGER',
          expiry_candles: n,
          replacement:    lc.replacement || 'REPLACE',
        },
      };
    } else {
      sections[key] = { enabled: true, tree: stripIds(tree) };
    }
  }

  const bullishLegs = (optionMapping.bullish || []).map((leg) => mapUiLegToExecutionLeg(leg, resolvedLotSize));
  const bearishLegs = (optionMapping.bearish || []).map((leg) => mapUiLegToExecutionLeg(leg, resolvedLotSize));

  // Optional scalar fields — omit the key entirely when empty rather than sending null
  const descriptionEntry  = formData.description  ? { description:    formData.description  } : {};
  const noEntryAfterEntry = formData.global_rules.no_entry_after
    ? { no_entry_after: formData.global_rules.no_entry_after }
    : {};

  // Build symbol_universe — prefer explicit universe state, fall back to legacy single-symbol
  const isExternal = strategySource === 'EXTERNAL';
  const suSymbols = symbolUniverse?.symbols?.length > 0
    ? symbolUniverse.symbols
    : (formData.symbol ? [formData.symbol] : []);
  const suMode    = symbolUniverse?.mode || 'SINGLE_SYMBOL';
  const suSegment = symbolUniverse?.segment  || null;
  const suPreset  = symbolUniverse?.preset_key || null;
  const externalPrimarySymbol = buildExternalPlaceholderSymbol(externalExchange || 'NSE');
  const forcedInstrument = (
    (isExternal && externalSegment === 'INDEX')
    || (!isExternal && suSegment === 'INDEX')
  ) ? 'OPTIONS' : formData.execution.instrument;
  const entryOrder = formData.execution?.entry_order || {};
  const entryOrderType = entryOrder.order_type || 'MARKET';
  const preferredExecutionMode = normalizePreferredExecutionMode(formData.preferred_execution_mode);
  const isLegacyEntryOrder = isLegacyStopConfig(entryOrder);
  const entryOrderPayload = {
    order_type: ({
      'SL-M': 'SLM',
      'SL-L': 'SL',
    }[entryOrderType] || entryOrderType),
    mpp: entryOrder.mpp ?? 2,
    price_mode: null,
    limit_price_value: null,
    trigger_price_value: null,
  };

  if (entryOrderType === 'SL-L') {
    entryOrderPayload.price_mode = entryOrder.price_mode || 'OFFSET_POINTS';
    entryOrderPayload.limit_price_value = toOptionalNumber(entryOrder.limit_price_value);
    if (isLegacyEntryOrder) {
      entryOrderPayload.trigger_price_value = toOptionalNumber(entryOrder.trigger_price_value);
    }
  } else if (entryOrderType === 'SL-M') {
    if (isLegacyEntryOrder) {
      entryOrderPayload.price_mode = entryOrder.price_mode || null;
      entryOrderPayload.trigger_price_value = toOptionalNumber(entryOrder.trigger_price_value);
    }
  } else if (entryOrderType === 'LIMIT' && isLegacyEntryOrder) {
    entryOrderPayload.price_mode = entryOrder.price_mode || null;
    entryOrderPayload.limit_price_value = toOptionalNumber(entryOrder.limit_price_value);
    entryOrderPayload.trigger_price_value = toOptionalNumber(entryOrder.trigger_price_value);
  }

  return {
    schema_version: '2.0',
    strategy_source: strategySource || 'INTERNAL',
    ...(isExternal && externalSourceType ? { external_source_type: externalSourceType } : {}),
    ...(isExternal && externalExchange ? { external_exchange: externalExchange } : {}),
    ...(isExternal && externalSegment ? { external_segment: externalSegment } : {}),
    ...(isExternal && externalBrokerName ? { external_broker_name: externalBrokerName } : {}),
    ...(isExternal && strategyToken ? { strategy_token: strategyToken } : {}),
    strategy_meta: {
      name:              formData.name,
      symbol:            isExternal ? externalPrimarySymbol : (suSymbols[0] || formData.symbol || ''),
      primary_timeframe: formData.primary_timeframe,
      ...descriptionEntry,
      ...(isExternal ? { external_mode: externalMode || 'SIMPLE' } : {}),
    },
    ...(!isExternal ? {
      symbol_universe: {
        mode:       suMode,
        symbols:    suSymbols,
        segment:    suSegment,
        ...(suPreset ? { preset_key: suPreset } : {}),
      },
    } : {}),
    sections,
    global_rules: {
      trading_mode:             formData.global_rules.trading_mode,
      start_time:               formData.global_rules.start_time,
      end_time:                 formData.global_rules.end_time,
      no_trade_windows:         formData.global_rules.no_trade_windows || [],
      ...noEntryAfterEntry,
      allow_long:               formData.global_rules.allow_long,
      allow_short:              formData.global_rules.allow_short,
      entry_conflict_policy:    formData.global_rules.entry_conflict_policy,
      opposite_signal_policy:   normalizeOppositeSignalHandling(formData.global_rules),
    },
    risk: {
      target: {
        enabled:  (Number(formData.risk.target.value) || 0) > 0,
        type:     formData.risk.target.type,
        value:    (Number(formData.risk.target.value) || 0) > 0 ? Number(formData.risk.target.value) : null,
        apply_on: formData.risk.target.apply_on || 'UNDERLYING',
      },
      stop_loss: {
        enabled:  (Number(formData.risk.stop_loss.value) || 0) > 0,
        type:     formData.risk.stop_loss.type,
        value:    (Number(formData.risk.stop_loss.value) || 0) > 0 ? Number(formData.risk.stop_loss.value) : null,
        apply_on: formData.risk.stop_loss.apply_on || 'UNDERLYING',
      },
      trailing_stop: {
        enabled:  (Number(formData.risk.trailing_stop.value) || 0) > 0,
        type:     formData.risk.trailing_stop.type,
        value:    (Number(formData.risk.trailing_stop.value) || 0) > 0 ? Number(formData.risk.trailing_stop.value) : null,
        apply_on: formData.risk.trailing_stop.apply_on || 'UNDERLYING',
      },
    },
    execution: {
      instrument:          forcedInstrument,
      allow_pyramiding: !!formData.execution?.allow_pyramiding,
      max_pyramid_legs: Math.min(20, Math.max(1, Number(formData.execution?.max_pyramid_legs) || 5)),
      product_type:       formData.execution?.product_type || defaultProductTypeForInstrument(formData.execution?.instrument),
      trade_in_next_exp:  forcedInstrument === 'OPTIONS'
        ? normalizeTradeInNextExp(formData.execution?.trade_in_next_exp)
        : 'OFF',
      entry_order: entryOrderPayload,
      ...((() => {
        const qPayload = normalizeQuantityPayload(formData.execution?.quantity);
        const allowPartialExit = !!formData.execution?.allow_partial_exit;
        if (qPayload) {
          return { quantity: { ...qPayload, allow_partial_exit: allowPartialExit } };
        }
        return allowPartialExit ? { quantity: { allow_partial_exit: allowPartialExit } } : {};
      })()),
      bullish_signal_legs: bullishLegs,
      bearish_signal_legs: bearishLegs,
    },
    ui_state: {
      scheduler: {
        enabled:   formData.scheduler_enabled,
        days_mask: formData.schedule_days_mask,
      },
      ...(preferredExecutionMode
        ? { preferred_execution_mode: preferredExecutionMode }
        : {}),
    },
  };
}

/**
 * Pure eligibility rule for the standard External partial-exit toggle.
 * Enabled only for BY_ALERT + non-pyramiding. Options-inclusive (does NOT
 * exclude Options). The Options-pyramiding toggle itself is unaffected.
 *
 * @param {{ quantityMode: string, allowPyramiding: boolean }} args
 * @returns {boolean}
 */
export function isPartialExitToggleEnabled({ quantityMode, allowPyramiding }) {
  return quantityMode === 'BY_ALERT' && !allowPyramiding;
}

/**
 * Pure eligibility rule for the Pyramiding toggle.
 * Disabled for OPTIONS while the backend field/capability is preserved for
 * possible future reintroduction. Enabled for UNDERLYING.
 *
 * @param {{ tradeIn: string }} args
 * @returns {boolean}
 */
export function isPyramidingToggleEnabled({ tradeIn }) {
  return tradeIn !== 'OPTIONS';
}

/**
 * Resolves allow_partial_exit from a saved execution payload using
 * nested-first resolution with legacy top-level fallback.
 * - Nested (execution.quantity.allow_partial_exit) takes precedence.
 * - Falls back to legacy top-level (execution.allow_partial_exit) if nested absent.
 * - Defaults to false if neither is present.
 *
 * @param {object|undefined} quantity - execution.quantity from saved payload
 * @param {boolean|undefined} legacyApe - execution.allow_partial_exit from saved payload
 * @returns {boolean}
 */
export function resolveAllowPartialExit(quantity, legacyApe) {
  if (quantity && typeof quantity.allow_partial_exit === 'boolean') {
    return quantity.allow_partial_exit;
  }
  if (typeof legacyApe === 'boolean') {
    return legacyApe;
  }
  return false;
}
